import { getLanguage, onLanguageChange, setLang, t } from '../i18n.js';

/**
 * Подписи, написанные прямо в разметке, — на выбранном языке.
 *
 * ⚠️ ПЕРЕВОД ПОВТОРЯЕМ, А НЕ ПЕРЕЗАГРУЖАЕМ СТРАНИЦУ. Раньше он лежал
 * в безымянном блоке main.ts: выполнялся один раз при загрузке модуля,
 * позвать заново было нельзя — и смену языка делали перезагрузкой.
 *
 * ⚠️ ПЕРЕЗАГРУЗОК БЫЛО ДВЕ, И ОНИ ЖЕ ЛОМАЛИ ПЕРЕКЛЮЧЕНИЕ. Кнопка звала
 * reload и setLanguage звала reload; вторая била по недогруженной первой,
 * модуль не доходил до перевода. Выходило худшее: язык уже сменился, а
 * форма осталась на прежнем — «нажал, и ничего, только что-то грузит».
 *
 * Здесь пишутся только textContent и placeholder. Обработчики не трогаются,
 * поэтому повторять можно сколько угодно раз.
 */

/**
 * Подписи экрана входа — на выбранном языке.
 *
 * ⚠️ БЕЗ ЭТОГО ВЫБОР ЯЗЫКА НА ВХОДЕ БЕССМЫСЛЕН. Разметка формы написана
 * по-русски прямо в index.html; переключив язык, игрок увидел бы ту же
 * русскую форму и решил, что кнопки не работают.
 */
export function applyStaticTexts(): void {
  const put = (id: string, key: string): void => {
    const e = document.getElementById(id);
    const s = t(key);
    if (e !== null && s !== key) e.textContent = s;
  };
  const hold = (id: string, key: string): void => {
    const e = document.getElementById(id) as HTMLInputElement | null;
    const s = t(key);
    if (e !== null && s !== key) e.placeholder = s;
  };
  put('auth-sub', 'auth.subtitle');
  put('auth-tab-login', 'auth.tab_login');
  put('auth-tab-register', 'auth.tab_register');
  put('auth-login-submit', 'auth.login_btn');
  // ⚠️ РАЗДЕЛИТЕЛЬ И КНОПКА ГОСТЯ — ТОЖЕ СЮДА. Обе написаны в разметке
  // по-русски; забудь их здесь — и испанец увидит посреди своей формы
  // «или» и «Гостевой вход», как это уже было с курсивными подсказками.
  put('auth-or', 'auth.or');
  put('auth-guest', 'auth.guest');
  put('auth-reg-submit', 'auth.register_btn');
  // ⚠️ КУРСИВНЫЕ ПОДСКАЗКИ ТОЖЕ ЗДЕСЬ, И ИХ ЗАБЫЛИ. Обе — про имя и про
  // приватность почты — были дописаны в разметку по-русски и в этот список не
  // попали. Пропажа не ломала ничего: форма работала, украинец видел
  // «Реєстрація», «Пошта», «Створити акаунт» — и посреди них русское «Имя» с
  // русскими пояснениями. Список и есть единственное место, где решается, что
  // переводится, а что нет.
  put('auth-hint-name', 'auth.name_hint');
  put('auth-hint-email', 'auth.email_hint');
  put('auth-hint-pass', 'auth.pass_hint');
  put('auth-hint-pass2', 'auth.pass2_hint');
  hold('auth-reg-name', 'auth.name_hold');
  hold('auth-reg-pass', 'auth.pass_min');
  // Подписи полей лежат в label вокруг input — берём первый текстовый узел.
  const labelOf = (inputId: string, key: string): void => {
    const inp = document.getElementById(inputId);
    const lab = inp?.closest('label');
    const s = t(key);
    if (lab !== null && lab !== undefined && s !== key && lab.firstChild !== null) {
      lab.firstChild.textContent = s;
    }
  };
  labelOf('auth-login-email', 'auth.email');
  labelOf('auth-login-pass', 'auth.password');
  labelOf('auth-reg-name', 'auth.name');
  labelOf('auth-reg-email', 'auth.email');
  labelOf('auth-reg-pass', 'auth.password');
  labelOf('auth-reg-pass2', 'auth.password2');
}

// ⚠️ ПЕРЕВОД ПОВТОРЯЕМ, А НЕ ПЕРЕЗАГРУЖАЕМ СТРАНИЦУ. Здесь стоял безымянный
// блок: он выполнялся один раз при загрузке модуля, и позвать его заново было
// нельзя — потому смену языка и делали перезагрузкой. Все вызовы внутри пишут
// только textContent и placeholder, обработчиков не трогают, и повторять их
// можно сколько угодно.
applyStaticTexts();
onLanguageChange(applyStaticTexts);

/**
 * Кнопки языка на экране входа: подписи и выделение выбранного.
 *
 * ⚠️ ОТДЕЛЬНО ОТ МЕНЮ, И ЭТО НЕ ДУБЛИРОВАНИЕ. В меню язык — настройка среди
 * прочих; здесь он нужен раньше всего остального. Подписи самой формы уже
 * на каком-то языке, и человек, который его не читает, не поймёт ни полей,
 * ни кнопки, а меню ещё надо догадаться открыть.
 *
 * ⚠️ ВЫДЕЛЕНИЕ ПЕРЕСТАВЛЯЕТСЯ НА КАЖДОЙ СМЕНЕ, А НЕ СТАВИТСЯ ОДИН РАЗ.
 * Выделение ставилось один раз при разборе кнопок, а рядом стояло пояснение:
 * «setLang сам перезагружает страницу, применять ничего не нужно».
 * Перезагрузку убрали — и выделение осталось на прежнем языке: подписи уже
 * украинские, а обведён RUS. Ровно на это и пожаловались.
 */
function markCurrentLanguage(): void {
  const box = document.getElementById('auth-langs');
  if (box === null) return;
  const now = getLanguage();
  for (const b of Array.from(box.querySelectorAll('button'))) {
    b.classList.toggle('on', (b.dataset.lang ?? '') === now);
  }
}

function bindLanguageButtons(): void {
  const box = document.getElementById('auth-langs');
  if (box === null) return;
  for (const b of Array.from(box.querySelectorAll('button'))) {
    const code = b.dataset.lang ?? '';
    b.addEventListener('click', () => {
      if (code !== '' && code !== getLanguage()) setLang(code as 'ru' | 'en' | 'uk' | 'es');
    });
  }
}

bindLanguageButtons();
markCurrentLanguage();
onLanguageChange(markCurrentLanguage);
